import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Upload, Plus, Search, RefreshCw, FileSpreadsheet,
  ArrowRight, Coffee, Salad, Dumbbell, Mountain, X, Loader2,
  CheckCircle2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PhoneInput from "@/components/PhoneInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// The pipeline choices the "Move to…" dropdown offers. Keep in sync with
// backend `PIPELINES` (routes/growth.py).
const MOVE_TARGETS = [
  { key: "b2c_coffee",   label: "B2C Coffee",      icon: Coffee,   accent: "text-amber-600" },
  { key: "b2c_jaydiets", label: "B2C JAYDIETs",    icon: Salad,    accent: "text-emerald-600" },
  { key: "b2c_wellness", label: "B2C Wellness",    icon: Dumbbell, accent: "text-fuchsia-600" },
  { key: "b2c_retreat",  label: "B2C Retreat",     icon: Mountain, accent: "text-sky-600" },
];

export default function B2CIntelHome() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selection, setSelection] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pipeline: "b2c_intel", limit: "500" });
      if (q.trim()) params.set("search", q.trim());
      const { data } = await api.get(`/growth/leads?${params.toString()}`);
      setLeads(data.items || []);
    } catch (e) {
      toast.error("Failed to load B2C Intelligence leads");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const selectedIds = Object.keys(selection);
  const bumpRefresh = () => setRefreshKey((n) => n + 1);

  const moveToPipeline = async (targetKey) => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post("/growth/leads/bulk-move", {
        lead_ids: selectedIds,
        to_pipeline: targetKey,
      });
      toast.success(`Moved ${data.moved} lead${data.moved === 1 ? "" : "s"} → ${targetKey.replace("b2c_", "")}`);
      setSelection({});
      bumpRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const toggleAll = () => {
    if (selectedIds.length === leads.length) setSelection({});
    else setSelection(Object.fromEntries(leads.map((l) => [l.id, true])));
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="b2c-intel-home">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary inline-flex items-center gap-2">
            <Users className="h-3 w-3" /> B2C Lead Intelligence · Staging pool
          </div>
          <h1 className="font-display text-4xl mt-1">B2C leads land here first</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Upload from Apollo, LinkedIn Sales Navigator, Google Maps scrapers, or Instagram exports —
            we normalise, dedupe, and stage every lead here. Pick the right pipeline
            (Coffee / JAYDIETs / Wellness / Retreat) and move them across when you're ready to outreach.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full gap-1.5" onClick={() => setAddOpen(true)} data-testid="b2c-add-btn">
            <Plus className="h-3.5 w-3.5" /> Add lead
          </Button>
          <Button className="rounded-full gap-1.5" onClick={() => setUploadOpen(true)} data-testid="b2c-upload-btn">
            <Upload className="h-3.5 w-3.5" /> Upload CSV / Excel
          </Button>
        </div>
      </div>

      {/* Selection bar */}
      {selectedIds.length > 0 && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 flex items-center gap-3 flex-wrap" data-testid="b2c-selection-bar">
          <div className="text-sm font-medium">
            {selectedIds.length} selected
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-full gap-1.5 ml-auto" data-testid="b2c-move-btn">
                Move to pipeline <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Move {selectedIds.length} lead{selectedIds.length === 1 ? "" : "s"} to…</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {MOVE_TARGETS.map((t) => (
                <DropdownMenuItem key={t.key} onClick={() => moveToPipeline(t.key)} data-testid={`b2c-move-to-${t.key}`}>
                  <t.icon className={"h-4 w-4 mr-2 " + t.accent} /> {t.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelection({})} data-testid="b2c-clear-selection">
            Clear
          </Button>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search company, email, city…"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-border bg-background text-sm"
            data-testid="b2c-search"
          />
        </div>
        <Button variant="ghost" size="sm" className="rounded-full" onClick={load} data-testid="b2c-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">
          {leads.length} lead{leads.length === 1 ? "" : "s"} in staging
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <FileSpreadsheet className="h-6 w-6 mx-auto mb-2 text-primary opacity-60" />
          <div className="font-medium">No B2C leads in staging</div>
          <div className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Upload a CSV/Excel from your favourite scraper or add a lead manually to get started.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={leads.length > 0 && selectedIds.length === leads.length}
                    onChange={toggleAll}
                    data-testid="b2c-select-all"
                  />
                </th>
                <th className="text-left px-3 py-3">Business</th>
                <th className="text-left px-3 py-3">City</th>
                <th className="text-left px-3 py-3">Industry</th>
                <th className="text-left px-3 py-3">Source</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`b2c-row-${l.id}`}>
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={!!selection[l.id]}
                      onChange={(e) => setSelection((s) => {
                        const next = { ...s };
                        if (e.target.checked) next[l.id] = true; else delete next[l.id];
                        return next;
                      })}
                      data-testid={`b2c-check-${l.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/app/growth/leads/${l.id}`} className="font-medium hover:underline">
                      {l.business_name}
                    </Link>
                    {l.decision_maker_name && (
                      <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">
                        {l.decision_maker_name}{l.decision_maker_role ? ` · ${l.decision_maker_role}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{l.city || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground text-xs">{l.industry || "—"}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-secondary/60">{l.source || "—"}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={l.status || "new"} />
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[220px]">{l.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onImported={() => { setUploadOpen(false); bumpRefresh(); }}
        />
      )}
      {addOpen && (
        <AddLeadDialog
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); bumpRefresh(); }}
        />
      )}
    </div>
  );
}


/* -------------------- Status badge (shared) -------------------- */

export function StatusBadge({ status }) {
  const style = {
    new: "bg-secondary text-foreground",
    contacted: "bg-blue-500/15 text-blue-600 dark:text-blue-300",
    qualified: "bg-primary/15 text-primary",
    meeting: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300",
    won: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
    lost: "bg-destructive/15 text-destructive",
    disqualified: "bg-red-500/15 text-red-600 dark:text-red-300",
    deferred: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  }[status] || "bg-secondary";
  return (
    <span className={"text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full " + style}>
      {status}
    </span>
  );
}


/* -------------------- Upload dialog (CSV/Excel) -------------------- */

function UploadDialog({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dedupeOn, setDedupeOn] = useState("email");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const doPreview = async (f) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/b2c/upload/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    doPreview(f);
  };

  const doImport = async () => {
    if (!file || !preview) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify({ mapping, dedupe_on: dedupeOn }));
      const { data } = await api.post("/b2c/upload/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created} new · ${data.updated} updated · ${data.skipped_total} skipped`);
      if (data.created > 0 || data.updated > 0) {
        setTimeout(() => onImported && onImported(), 1200);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="b2c-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
            Upload leads · CSV, TSV, or XLSX (max 20 MB, 5000 rows)
          </DialogTitle>
        </DialogHeader>

        {!preview && !file && (
          <div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-primary/40 transition"
              data-testid="b2c-drop-zone"
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <div className="font-medium">Drop your file here or click to browse</div>
              <div className="text-xs text-muted-foreground mt-1">
                Works with Apollo, LinkedIn Sales Navigator, Google Maps scrapers,
                Instagram exports, Serper, etc.
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.tsv,.xlsx,.xlsm"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="b2c-file-input"
              />
            </div>
          </div>
        )}

        {busy && !preview && (
          <div className="text-center py-6 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Parsing file…
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              Found <b className="text-foreground">{preview.columns.length}</b> columns · showing {" "}
              <b className="text-foreground">{preview.sample_rows.length}</b> sample rows.
              Map each Kairosa field to a source column below — we've pre-filled sensible defaults.
            </div>

            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <th className="text-left px-3 py-2">Kairosa field</th>
                    <th className="text-left px-3 py-2">Source column</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.mappable_fields.map((f) => (
                    <tr key={f} className="border-b border-border last:border-0" data-testid={`b2c-map-row-${f}`}>
                      <td className="px-3 py-2 font-mono text-xs">{f}</td>
                      <td className="px-3 py-2">
                        <select
                          value={mapping[f] || ""}
                          onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || undefined }))}
                          className="w-full h-8 rounded border border-border bg-background px-2 text-xs"
                          data-testid={`b2c-map-${f}`}
                        >
                          <option value="">— skip —</option>
                          {preview.columns.filter((c) => c).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-md border border-border bg-secondary/30 p-3 flex items-center gap-3 flex-wrap">
              <label className="text-xs">Skip duplicates on:</label>
              <select value={dedupeOn} onChange={(e) => setDedupeOn(e.target.value)}
                      className="h-8 rounded border border-border bg-background px-2 text-xs"
                      data-testid="b2c-dedupe-select">
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="email_or_phone">Email OR Phone</option>
                <option value="none">Don't dedupe (import every row)</option>
              </select>
              <div className="text-[10px] text-muted-foreground ml-auto">
                Duplicate rows fill blank fields on the existing lead instead of overwriting.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => { setFile(null); setPreview(null); }}>Choose another file</Button>
              <Button onClick={doImport} disabled={busy} data-testid="b2c-import-btn">
                {busy ? "Importing…" : "Import leads"}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="b2c-import-result">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">Import complete</span>
            </div>
            <div className="mt-2 grid grid-cols-4 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Created</div><div className="text-xl font-semibold tabular-nums">{result.created}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Updated</div><div className="text-xl font-semibold tabular-nums">{result.updated}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Skipped</div><div className="text-xl font-semibold tabular-nums">{result.skipped_total}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Total rows</div><div className="text-xl font-semibold tabular-nums">{result.total_rows_processed}</div></div>
            </div>
            {result.skipped?.length > 0 && (
              <div className="mt-3 text-xs">
                <div className="text-muted-foreground mb-1">First few skipped:</div>
                <ul className="list-disc list-inside space-y-0.5 text-[11px] text-muted-foreground">
                  {result.skipped.map((s, i) => (
                    <li key={i}>Row {s.row}: {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* -------------------- Add lead dialog -------------------- */

function AddLeadDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({
    business_name: "", email: "", phone: "", city: "", country: "",
    industry: "", website: "", decision_maker_name: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/b2c/leads", form);
      toast.success("Lead added to staging");
      onAdded && onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="b2c-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-normal">
            Add a lead to B2C Intelligence
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {[
            ["business_name", "Business / Full name"],
            ["decision_maker_name", "Contact name (optional)"],
            ["email", "Email"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-xs">{label}</label>
              <input
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                data-testid={`b2c-add-${key}`}
              />
            </div>
          ))}

          <div>
            <label className="text-xs">Phone</label>
            <div className="mt-1">
              <PhoneInput
                value={form.phone}
                defaultCountry={form.country}
                onChange={(v) => setForm({ ...form, phone: v })}
                testId="b2c-add-phone"
              />
            </div>
          </div>

          {[
            ["city", "City"],
            ["country", "Country"],
            ["industry", "Industry"],
            ["website", "Website"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-xs">{label}</label>
              <input
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm"
                data-testid={`b2c-add-${key}`}
              />
            </div>
          ))}
          <div>
            <label className="text-xs">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="b2c-add-notes"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="b2c-add-submit">
              {saving ? "Adding…" : "Add lead"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
